import { NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Injector, Input, Output } from '@angular/core';
import OfferCardComponent from '@app/main/dashboard/offer-card/offer-card.component';
import SearchCardComponent from '@app/main/dashboard/search-card/search-card.component';
import { ClientActivityLocalizationKeys, OfferStatusesNames, SearchStatusesNames } from '@shared/AppConsts';
import AppComponentBase from '@shared/common/app-component-base';
import { ISearchInterestSurveyAction, nullable } from '@shared/common/common-types';
import SearchStatusClassPipe from '@shared/common/pipes/search-status-class.pipe';
import {
    OfferOfClientResponseModel,
    ProjectOfClientResponseModel,
    SearchOfClientResponseModel,
} from '@shared/service-proxies/service-proxies';
import UtilsModule from '@shared/utils/utils.module';
import { isEmpty } from 'lodash';

import ProjectSummaryCardComponent from './project-summary-card/project-summary-card.component';

@Component({
    selector: 'app-client-offers-searches-projects',
    imports: [
        UtilsModule,
        NgTemplateOutlet,
        OfferCardComponent,
        SearchCardComponent,
        ProjectSummaryCardComponent,
        SearchStatusClassPipe,
    ],
    templateUrl: './client-offers-searches-projects.component.html',
    styleUrl: './client-offers-searches-projects.component.less',
})
class ClientOffersSearchesProjectsComponent extends AppComponentBase {
    @Input() offers: OfferOfClientResponseModel[] = [];

    @Input() searches: SearchOfClientResponseModel[] = [];

    @Input() projects: ProjectOfClientResponseModel[] = [];

    @Input() tenantId: nullable<number>;

    @Input() disableOnClick: boolean = false;

    @Input() showSurveyActions: boolean = false;

    @Output() surveyActionClicked = new EventEmitter<ISearchInterestSurveyAction>();

    protected readonly ClientActivityLocalizationKeys = ClientActivityLocalizationKeys;

    constructor(injector: Injector) {
        super(injector);
    }

    get hasOffers(): boolean {
        return !isEmpty(this.offers);
    }

    get hasSearches(): boolean {
        return !isEmpty(this.searches);
    }

    get hasProjects(): boolean {
        return !isEmpty(this.projects);
    }

    get hasActivity(): boolean {
        return this.hasOffers || this.hasSearches || this.hasProjects;
    }

    get activeOffers(): OfferOfClientResponseModel[] {
        return this.offers.filter((offer) => !this.isArchivedOffer(offer));
    }

    get archivedOffers(): OfferOfClientResponseModel[] {
        return this.offers.filter((offer) => this.isArchivedOffer(offer));
    }

    get activeSearches(): SearchOfClientResponseModel[] {
        return this.searches.filter((search) => !this.isArchivedSearch(search));
    }

    get archivedSearches(): SearchOfClientResponseModel[] {
        return this.searches.filter((search) => this.isArchivedSearch(search));
    }

    // The heading label falls back to archive/deal when the column holds nothing active at all,
    // in which case there is only one group and no divider is drawn.
    get offersHeaderLocalizationKey(): string {
        return isEmpty(this.activeOffers)
            ? ClientActivityLocalizationKeys.ArchivedDeals
            : ClientActivityLocalizationKeys.Active;
    }

    get searchesHeaderLocalizationKey(): string {
        return isEmpty(this.activeSearches)
            ? ClientActivityLocalizationKeys.ArchivedDeals
            : ClientActivityLocalizationKeys.Active;
    }

    get showOffersSeparator(): boolean {
        return !isEmpty(this.activeOffers) && !isEmpty(this.archivedOffers);
    }

    get showSearchesSeparator(): boolean {
        return !isEmpty(this.activeSearches) && !isEmpty(this.archivedSearches);
    }

    private isArchivedOffer(offer: OfferOfClientResponseModel): boolean {
        return [OfferStatusesNames.Archive, OfferStatusesNames.Deal].includes(offer.offerStatusName ?? '');
    }

    private isArchivedSearch(search: SearchOfClientResponseModel): boolean {
        return [SearchStatusesNames.Archive, SearchStatusesNames.Deal].includes(search.searchStatusName ?? '');
    }
}

export default ClientOffersSearchesProjectsComponent;
